import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';
import { getInboundTransferRequestsCacheKey as inboundTransferRequestsCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useMedicalProfiles from './useMedicalProfiles';
import useUser from './useUser';

const intl = getIntl('useInboundTransferRequests', {
    errorLoadingTransferRequests: 'Error occurred loading my transfer requests',
});

export type UseInboundTransferRequestsReturnValue = {
    inboundTransferRequests: APITypes.medicalProfileService.InboundTransferRequest[];
    isLoadingInboundTransferRequests: boolean;
};

export default function useInboundTransferRequests(): UseInboundTransferRequestsReturnValue {
    const { user } = useUser({});
    const inboundTransferRequestsQuery = useQuery({
        queryKey: inboundTransferRequestsCacheKey(),
        queryFn: async () => {
            const response = (await appAPI.get(
                urls.api.ownershipTransfer.myInboundTransferRequests,
            )) as APITypes.medicalProfileService.InboundTransferRequestsResponse;
            return response.inboundTransferRequests;
        },
        enabled: user !== 'loading' && user.type !== 'professional',
    });
    useQueryErrorNotify(inboundTransferRequestsQuery, intl('errorLoadingTransferRequests'));

    return {
        inboundTransferRequests: inboundTransferRequestsQuery.data || [],
        isLoadingInboundTransferRequests: inboundTransferRequestsQuery.isLoading,
    };
}

/**
 * Determines if the user has a pending transfer request from a profile they are not a collaborator of
 */
export function useInboundExternalTransferRequest(): {
    hasInboundExternalTransferRequest: boolean;
    inboundExternalTransferRequest: APITypes.medicalProfileService.InboundTransferRequest | null;
} {
    const { collaboratorMedicalProfiles, isLoadingProfiles } = useMedicalProfiles({});
    const { inboundTransferRequests, isLoadingInboundTransferRequests } = useInboundTransferRequests();

    // sort it to make it deterministic.  They could have multiple pending requests, so ensure they're always focused on the same one
    const sortedInboundTransferRequests = inboundTransferRequests.sort((a, b) => {
        return a.medicalProfileOwnershipTransferId.localeCompare(b.medicalProfileOwnershipTransferId);
    });

    if (isLoadingProfiles || isLoadingInboundTransferRequests)
        return { hasInboundExternalTransferRequest: false, inboundExternalTransferRequest: null };

    const collaboratorProfileIds = collaboratorMedicalProfiles.map((_) => _.medicalProfileId).filter(Boolean);
    const inboundTransferRequest = sortedInboundTransferRequests.find(
        (_) => !collaboratorProfileIds.includes(_.profile.medicalProfileId),
    );

    return {
        hasInboundExternalTransferRequest: !!inboundTransferRequest,
        inboundExternalTransferRequest: inboundTransferRequest || null,
    };
}
